
import { useEffect, useState, useRef } from "react";
import "./Adminpannel.css";
import "bootstrap-icons/font/bootstrap-icons.css";

export default function AdminPanel() {
  const [allUsers, setAllUsers] = useState([]);
  const [paidUsers, setPaidUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [globalSearch, setGlobalSearch] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  const [activeFilter, setActiveFilter] = useState({
    table: null,
    column: null,
  });
  const [filterValues, setFilterValues] = useState({
    reg: {},
    paid: {},
  });

  const [selectedUser, setSelectedUser] = useState(null);
  const [showModal, setShowModal] = useState(false);

  const [selectedPaid, setSelectedPaid] = useState(null);
  const [showPaidModal, setShowPaidModal] = useState(false);

  const [dropdownIndex, setDropdownIndex] = useState(null);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwordEmail, setPasswordEmail] = useState("");
  const [newPassword, setNewPassword] = useState("");

  const popupRef = useRef(null);

  // ✅ Pagination States
  const [regPage, setRegPage] = useState(1);
  const [paidPage, setPaidPage] = useState(1);
  const rowsPerPage = 10;

  // ✅ Fetch all data
  const fetchData = async () => {
    try {
      setLoading(true);
      const res = await fetch("https://agepcoatings.com/iccigetadminpanel");
      const data = await res.json();
      setAllUsers(data.allUsers || []);
      setPaidUsers(data.paidUsers || []);
    } catch {
      setError("Failed to load admin panel data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();

    const handleClickOutside = (e) => {
      if (popupRef.current && !popupRef.current.contains(e.target)) {
        setActiveFilter({ table: null, column: null });
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const regColumns = [
    "name",
    "email",
    "phone",
    "member",
    "memberNumber",
    "date",
    "userType",
  ];

  const paidColumns = [
    "name",
    "email",
    "courses",
    "level ID",
    "Gst Amount",
    "User Paid",
    "date",
  ];

  // ✅ Filtering logic
  const filterTable = (data, tableKey) => {
    return data.filter((row) => {
      const matchesGlobal = Object.values(row)
        .join(" ")
        .toLowerCase()
        .includes(globalSearch.toLowerCase());

      const created = new Date(row.date);
      const matchesFrom = fromDate ? created >= new Date(fromDate) : true;
      const matchesTo = toDate ? created <= new Date(toDate) : true;

      const tableFilters = filterValues[tableKey] || {};

      const matchesCols = Object.entries(tableFilters).every(([col, val]) => {
        if (!val) return true;
        const searchVal = val.toLowerCase();

        let cellValue = "";

        if (tableKey === "paid") {
          switch (col) {
            case "name":
              cellValue = row.name || "";
              break;
            case "email":
              cellValue = row.email || "";
              break;
            case "courses":
              if (Array.isArray(row.courses)) {
                cellValue = row.courses
                  .map((c) => c.courseTitle || "")
                  .join(" ")
                  .toLowerCase();
              }
              break;
            case "level ID":
              if (Array.isArray(row.courses)) {
                cellValue = row.courses
                  .map((c) => c.levelId || "")
                  .join(" ")
                  .toLowerCase();
              }
              break;
            case "Gst Amount":
              cellValue = (
                row?.gst?.gstAmount ||
                row?.gst?.amount ||
                row?.totalGst ||
                0
              ).toString();
              break;
            case "User Paid":
              const totalPaid = (row.courses || []).reduce((sum, c) => {
                if (c.mode === "partial") return sum + (Number(c.paid) || 0);
                if (c.mode === "full" || c.mode === "remaining")
                  return sum + (Number(c.remainAmount) || 0);
                return sum;
              }, 0);
              const totalAmount =
                row?.gst?.totalAmount || row?.totalAmount || 0;
              cellValue = `${totalPaid} ${totalAmount}`.toLowerCase();
              break;
            case "date":
              cellValue = new Date(row.date).toLocaleDateString().toLowerCase();
              break;
            default:
              cellValue = (row[col] || "").toString().toLowerCase();
          }
        } else {
          cellValue = (row[col] || "").toString().toLowerCase();
        }

        return cellValue.includes(searchVal);
      });

      return matchesGlobal && matchesFrom && matchesTo && matchesCols;
    });
  };

  const filteredAllUsers = filterTable(allUsers, "reg");
  const filteredPaidUsers = filterTable(paidUsers, "paid");

  // ✅ Pagination logic
  const paginate = (data, page) => {
    const start = (page - 1) * rowsPerPage;
    return data.slice(start, start + rowsPerPage);
  };

  const regTotalPages = Math.ceil(filteredAllUsers.length / rowsPerPage);
  const paidTotalPages = Math.ceil(filteredPaidUsers.length / rowsPerPage);

  const paginatedRegUsers = paginate(filteredAllUsers, regPage);
  const paginatedPaidUsers = paginate(filteredPaidUsers, paidPage);

  const Pagination = ({ page, totalPages, setPage }) => {
    if (totalPages <= 1) return null;
    const pages = [];
    for (let i = 1; i <= totalPages; i++) pages.push(i);
    return (
      <div className="pagination-controls mb-3">
        <button
          disabled={page === 1}
          onClick={() => setPage(page - 1)}
          className="btn btn-sm btn-outline-secondary me-1"
        >
          Prev
        </button>
        {pages.map((p) => (
          <button
            key={p}
            className={`btn btn-sm ${
              p === page ? "btn-primary" : "btn-outline-primary"
            } me-1`}
            onClick={() => setPage(p)}
          >
            {p}
          </button>
        ))}
        <button
          disabled={page === totalPages}
          onClick={() => setPage(page + 1)}
          className="btn btn-sm btn-outline-secondary"
        >
          Next
        </button>
      </div>
    );
  };

  // ✅ View handlers
  const handleViewUser = async (uid) => {
    try {
      const res = await fetch(
        `https://agepcoatings.com/iccigetuserdetails/${uid}`
      );
      const data = await res.json();
      setSelectedUser(data);
      setShowModal(true);
    } catch {
      console.error("Failed to load user details");
    }
  };

  const handleViewPaid = (uid, date) => {
    const invoice = paidUsers.find((i) => i.uid === uid && i.date === date);
    if (!invoice) return;
    setSelectedPaid(invoice);
    setShowPaidModal(true);
  };

  const closeFilter = () => {
    if (activeFilter.table) {
      setFilterValues((prev) => ({
        ...prev,
        [activeFilter.table]: {},
      }));
    }
    setActiveFilter({ table: null, column: null });
  };

  const handleUpdatePassword = async () => {
    if (!passwordEmail || !newPassword) return alert("Enter all fields");

    try {
      const res = await fetch("https://agepcoatings.com/updatepassword", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: passwordEmail, newPassword }),
      });

      const data = await res.json();
      if (data.success) {
        alert("Password updated successfully!");
        setShowPasswordModal(false);
        setNewPassword("");
        setPasswordEmail("");
      } else {
        alert(data.error || "Update failed");
      }
    } catch {
      alert("Error updating password");
    }
  };

  const renderCell = (row, col) => {
    if (col === "date") return new Date(row[col]).toLocaleDateString();
    if (col === "courses") {
      if (Array.isArray(row.courses) && row.courses.length > 0) {
        const uniqueCourses = [
          ...new Set(row.courses.map((c) => c.courseTitle || "N/A")),
        ];
        return (
          <ul style={{ margin: 0, paddingLeft: "15px" }}>
            {uniqueCourses.map((title, i) => (
              <li key={i}>{title}</li>
            ))}
          </ul>
        );
      }
      return "No Courses";
    }

    if (col === "level ID") {
      if (Array.isArray(row.courses))
        return row.courses.map((c) => c.levelId).join(", ");
      return row.levelId || "-";
    }

    if (col === "Gst Amount") {
      const gstAmount = row?.gst?.gstAmount || row?.gst?.amount || 0;
      return `₹${Number(gstAmount).toLocaleString("en-IN")}`;
    }

    if (col === "User Paid") {
      const courses = row.courses || [];
      const totalCoursePrice = courses.reduce((sum, c) => {
        if (c.mode === "partial") return sum + (Number(c.paid) || 0);
        if (c.mode === "full" || c.mode === "remaining")
          return sum + (Number(c.remainAmount) || 0);
        return sum;
      }, 0);
      const totalAmount = row?.gst?.totalAmount || row?.totalAmount || 0;
      return (
        <div>
          <div>
            <strong>Paid:</strong> ₹{totalCoursePrice.toLocaleString("en-IN")}
          </div>
          <div>
            <strong>Total Amount:</strong> ₹
            {Number(totalAmount).toLocaleString("en-IN")}
          </div>
        </div>
      );
    }

    return row[col];
  };


  const renderTable = (columns, data, tableKey, viewHandler) => (
    <div className="table-section">
      <div className="table-responsive custom-table">
        <table className="table table-bordered table-hover">
          <thead>
            <tr>
              {columns.map((col) => (
                <th key={col} className="position-relative">
                  <div className="d-flex align-items-center justify-content-between">
                    <span className="text-capitalize">{col}</span>
                    <i
                      className="bi bi-funnel-fill filter-icon"
                      onClick={() =>
                        setActiveFilter(
                          activeFilter.column === col &&
                            activeFilter.table === tableKey
                            ? { table: null, column: null }
                            : { table: tableKey, column: col }
                        )
                      }
                    ></i>
                  </div>
                  {activeFilter.column === col &&
                    activeFilter.table === tableKey && (
                      <div ref={popupRef} className="filter-popup shadow-sm">
                        <input
                          type="text"
                          placeholder={`Filter by ${col}`}
                          value={filterValues[tableKey]?.[col] || ""}
                          onChange={(e) =>
                            setFilterValues({
                              ...filterValues,
                              [tableKey]: {
                                ...filterValues[tableKey],
                                [col]: e.target.value,
                              },
                            })
                          }
                        />
                        <i
                          className="bi bi-x-lg close-popup"
                          onClick={closeFilter}
                        ></i>
                      </div>
                    )}
                </th>
              ))}
              <th>Action</th>
            </tr>
          </thead>

          <tbody>
            {data.map((row, i) => (
              <tr key={i}>
                {columns.map((col) => (
                  <td
                    key={col}
                    onClick={() => viewHandler(row.uid, row.date)}
                    className="cursor-pointer"
                  >
                    {renderCell(row, col)}
                  </td>
                ))}

                <td className="position-relative">
                  <i
                    className="bi bi-three-dots-vertical action-icon"
                    onClick={() =>
                      setDropdownIndex(dropdownIndex === i ? null : i)
                    }
                  ></i>

                  {dropdownIndex === i && (
                    <div className="dropdown-menu show shadow-sm">
                      <button
                        className="dropdown-item"
                        onClick={() => {
                          setDropdownIndex(null);
                          viewHandler(row.uid, row.date);
                        }}
                      >
                        <i className="bi bi-eye me-2"></i> View
                      </button>

                      <button
                        className="dropdown-item"
                        onClick={() => {
                          setDropdownIndex(null);
                          setPasswordEmail(row.email);
                          setShowPasswordModal(true);
                        }}
                      >
                        <i className="bi bi-key me-2"></i> Update Password
                      </button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  if (loading) return <p className="message">Loading...</p>;
  if (error) return <p className="message error">{error}</p>;

  return (
    <div className="admin-panel container-fluid">
      <h3 className="mt-5">Admin Panel</h3>

      {/* ===== Global Header ===== */}
      <div className="global-header mb-3">
        <input
          type="text"
          placeholder="Global Search..."
          value={globalSearch}
          onChange={(e) => setGlobalSearch(e.target.value)}
        />
        <input
          type="date"
          value={fromDate}
          onChange={(e) => setFromDate(e.target.value)}
        />
        <span>to</span>
        <input
          type="date"
          value={toDate}
          onChange={(e) => setToDate(e.target.value)}
        />
        <button
          onClick={() => {
            setGlobalSearch("");
            setFromDate("");
            setToDate("");
            setFilterValues({ reg: {}, paid: {} });
            setRegPage(1);
            setPaidPage(1);
          }}
          className="cler-btn"
        >
          Clear
        </button>
      </div>

      <h2>Registered Users</h2>
      {renderTable(regColumns, paginatedRegUsers, "reg", handleViewUser)}
      <Pagination
        page={regPage}
        totalPages={regTotalPages}
        setPage={setRegPage}
      />

      <h2>Paid Users</h2>
      {renderTable(paidColumns, paginatedPaidUsers, "paid", handleViewPaid)}
      <Pagination
        page={paidPage}
        totalPages={paidTotalPages}
        setPage={setPaidPage}
      />

      {/* ===== Modals ===== */}
      {showModal && selectedUser && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-end align-content-end">
              {" "}
              <button
                className="btn-close btncloseicon"
                onClick={() => setShowModal(false)}
              ></button>
            </div>
            <h3>User Details</h3>
            <div className="user-details">
              {Object.entries(selectedUser)
                .filter(([key]) => key !== "uid")
                .map(([k, v]) => (
                  <div key={k} className="user-row">
                    <strong>{k}</strong>
                    <span>{v?.toString() || "-"}</span>
                  </div>
                ))}
            </div>
            <button
              className="close-btn w-25"
              onClick={() => setShowModal(false)}
            >
              Close
            </button>
          </div>
        </div>
      )}

      {showPaidModal && selectedPaid && (
        <div className="modal-overlay" onClick={() => setShowPaidModal(false)}>
          <div
            className="modal-content full-width"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-end align-content-end">
              <button
                className="btn-close btncloseicon"
                onClick={() => setShowPaidModal(false)}
              ></button>
            </div>
            <h3>Paid User Details</h3>
            <div className="user-details">
              <div className="user-row">
                <strong>Email</strong>
                <span>{selectedPaid.email}</span>
              </div>
              <div className="user-row">
                <strong>Billing Name</strong>
                <span>{selectedPaid.billingDetails?.fullname}</span>
              </div>
              <div className="user-row">
                <strong>Address</strong>
                <span>{selectedPaid.billingDetails?.address}</span>
              </div>
              <div className="user-row">
                <strong>GST Number</strong>
                <span>{selectedPaid.gst?.number}</span>
              </div>
              <div className="user-row">
                <strong>GST Applied</strong>
                <span>{selectedPaid.gst?.applied ? "Yes" : "No"}</span>
              </div>
              <div className="user-row">
                <strong>Total Amount</strong>
                <span>{selectedPaid.totalAmount}</span>
              </div>
              <div className="user-row">
                <strong>Status</strong>
                <span>{selectedPaid.status}</span>
              </div>

              <h4>Courses</h4>
              {Array.isArray(selectedPaid.courses) &&
                selectedPaid.courses.map((c, idx) => (
                  <div key={idx} className="user-row course-row">
                    <strong>{c.courseTitle}</strong>
                    <span>
                      ID: {c.courseId}, Level: {c.levelId}, Mode: {c.mode},
                      Rate: {c.rate}, Paid: {c.paid}, Remain: {c.remainAmount}{" "}
                      {c.currency}
                    </span>
                  </div>
                ))}
            </div>
            <button
              className="close-btn w-25"
              onClick={() => setShowPaidModal(false)}
            >
              Close
            </button>
          </div>
        </div>
      )}

      {/* {showPasswordModal && (
        <div
          className="modal-overlay"
          onClick={() => setShowPasswordModal(false)}
        >
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Update Password</h3>
            <div className="form-group">
              <label>Email</label>
              <input
                type="email"
                value={passwordEmail}
                onChange={(e) => setPasswordEmail(e.target.value)}
                disabled
              />
            </div>
            <div className="form-group">
              <label>New Password</label>
              <input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
              />
            </div>
            <button className="btn btn-primary" onClick={handleUpdatePassword}>
              Update
            </button>
            <button
              className="btn btn-secondary ms-2"
              onClick={() => setShowPasswordModal(false)}
            >
              Cancel
            </button>
          </div>
        </div>
      )} */}

      {/* Update Password Modal */}
      {showPasswordModal && (
        <div
          className="modal-overlay"
          onClick={() => setShowPasswordModal(false)}
        >
          <div
            className="modal-content small-width"
            onClick={(e) => e.stopPropagation()}
          >
            <h3>Update Password</h3>
            <p>
              <strong>Email:</strong> {passwordEmail}
            </p>
            <input
              type="password"
              placeholder="Enter new password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
            />
            <div className="d-flex justify-content-end mt-3 gap-2">
              <button
                className="btn updatecancel"
                onClick={() => setShowPasswordModal(false)}
              >
                Cancel
              </button>
              <button
                className="btn updatepass"
                onClick={handleUpdatePassword}
              >
                Update
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
